/**
 * Natural-language schedule parsing (UI/UX review §8.1). Turns a Chinese phrase
 * ("每个工作日早上9点", "每天下午下班前", "每周一 10:30", "每月1号") into a cron +
 * a readable cadence label for the confirm card. Rule-based — no model call;
 * returns null when it can't tell, so the wizard falls back to the precise picker.
 * Pure (no imports) so it is unit-testable.
 */
export interface ParsedSchedule {
  /** 5-field cron the phrase resolves to. */
  cron: string;
  /** Human-readable cadence, e.g. "每个工作日 09:00". */
  label: string;
}

const ZH_DOW: Record<string, number> = { 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 日: 0, 天: 0, 七: 0 };

/** Parse a Chinese schedule phrase, or null when it cannot be understood. */
export function parseScheduleZh(input: string): ParsedSchedule | null {
  const t = input.trim();
  if (t.length === 0) return null;
  if (t.includes('每分钟')) return { cron: "* * * * *", label: "每分钟" };
  if (/每(个)?小时/.test(t)) return { cron: "0 * * * *", label: "每小时" };

  // Time of day (default 09:00 when a cadence is given without a clock time).
  let hour = 9;
  let minute = 0;
  let hasTime = false;
  const hm = /(\d{1,2})\s*[:：点时]\s*(\d{1,2})?/.exec(t);
  if (hm !== null) {
    hour = Number(hm[1]);
    hasTime = true;
    if (hm[2] !== undefined) minute = Number(hm[2]);
    else if (t.includes('半')) minute = 30;
  }
  if (/(下午|晚上|傍晚)/.test(t) && hour < 12) hour += 12;
  if (t.includes('凌晨') && hour === 12) hour = 0;
  if (t.includes('下班')) { hour = 18; minute = 0; hasTime = true; }
  if (hour > 23 || hour < 0 || minute > 59 || minute < 0) return null;
  const clock = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  if (/工作日|周一到周五|周一至周五|每个工作日/.test(t)) {
    return { cron: `${minute} ${hour} * * 1-5`, label: `每个工作日 ${clock}` };
  }
  const wk = /到|至/.test(t) ? null : (/(?:每周|周|星期|礼拜)\s*([一二三四五六日天七])/.exec(t));
  if (wk !== null) {
    const d = ZH_DOW[wk[1]!];
    const name = wk[1] === "天" || wk[1] === "日" ? "日" : wk[1];
    return { cron: `${minute} ${hour} * * ${d}`, label: `每周${name} ${clock}` };
  }
  const mo = /每月\s*(\d{1,2})\s*[号日]/.exec(t);
  if (mo !== null) {
    const dom = Number(mo[1]);
    if (dom >= 1 && dom <= 31) return { cron: `${minute} ${hour} ${dom} * *`, label: `每月 ${dom} 号 ${clock}` };
  }
  if (/每天|每日|天天/.test(t) || hasTime) {
    return { cron: `${minute} ${hour} * * *`, label: `每天 ${clock}` };
  }
  return null;
}
